<template>
  <AppHeaderBack>Change Password</AppHeaderBack>
  <div class="min-h-screen bg-gray-100 pt-8 pb-8 px-4">
    <div class="max-w-3xl mx-auto">
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div class="p-4 border-b border-gray-200 flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-xl text-primary">lock</span>
            <h2 class="text-lg font-semibold text-gray-800">Change Password</h2>
          </div>
        </div>
        
        <div class="p-6">
          <form @submit.prevent="updatePassword" class="space-y-6">
            <!-- Current Password -->
            <div>
              <label for="currentPassword" class="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span class="material-symbols-outlined text-gray-400">lock</span>
                </div>
                <input
                  id="currentPassword"
                  v-model="currentPassword"
                  :type="showCurrentPassword ? 'text' : 'password'"
                  placeholder="Enter your current password"
                  :disabled="loading"
                  class="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 transition duration-200 disabled:opacity-50"
                />
                <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button 
                    type="button" 
                    @click="showCurrentPassword = !showCurrentPassword"
                    class="text-gray-400 hover:text-gray-500 focus:outline-none"
                    :disabled="loading"
                  >
                    <span class="material-symbols-outlined text-xl">
                      {{ showCurrentPassword ? 'visibility_off' : 'visibility' }}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <!-- New Password -->
            <div>
              <label for="newPassword" class="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span class="material-symbols-outlined text-gray-400">key</span>
                </div>
                <input
                  id="newPassword"
                  v-model="newPassword"
                  :type="showNewPassword ? 'text' : 'password'"
                  placeholder="Enter your new password (min 6 characters)"
                  :disabled="loading"
                  class="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 transition duration-200 disabled:opacity-50"
                />
                <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button 
                    type="button" 
                    @click="showNewPassword = !showNewPassword"
                    class="text-gray-400 hover:text-gray-500 focus:outline-none"
                    :disabled="loading"
                  >
                    <span class="material-symbols-outlined text-xl">
                      {{ showNewPassword ? 'visibility_off' : 'visibility' }}
                    </span>
                  </button>
                </div>
              </div>
              <div v-if="newPassword.length > 0" class="mt-2">
                <div class="h-1 w-full bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    class="h-full transition-all duration-300"
                    :class="{
                      'bg-red-500': passwordStrength === 0,
                      'bg-yellow-500': passwordStrength === 1,
                      'bg-green-500': passwordStrength >= 2
                    }"
                    :style="{ width: passwordStrength * 33.33 + '%' }"
                  ></div>
                </div>
                <p class="text-xs mt-1 text-gray-500">
                  Password strength: 
                  <span :class="{
                    'text-red-500': passwordStrength === 0,
                    'text-yellow-500': passwordStrength === 1,
                    'text-green-500': passwordStrength >= 2
                  }">
                    {{ passwordStrengthText }}
                  </span>
                </p>
              </div>
            </div>

            <!-- Submit Button -->
            <button
              type="submit"
              :disabled="loading || passwordStrength < 0 || !currentPassword"
              class="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-lg font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-200 disabled:opacity-50"
            >
              <svg v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ loading ? "Updating..." : "Change Password" }}
            </button>

            <!-- Cancel Button -->
            <router-link 
              to="/profile"
              class="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-lg font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-200"
            >
              Cancel
            </router-link>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { updateUserPassword } from '@/composables/useAuth';
import AppHeaderBack from '@/components/common/AppHeaderBack.vue';

export default {
  components: { AppHeaderBack },
  data() {
    return { 
      newPassword: '', 
      currentPassword: '',
      showCurrentPassword: false,
      showNewPassword: false
    };
  },
  computed: { 
    ...mapState(['loading']),
    passwordStrength() {
      if (this.newPassword.length === 0) return -1;
      if (this.newPassword.length < 6) return 0;
      if (this.newPassword.length < 8) return 1;
      return 2; // Good enough for this example - could add more complex checks
    },
    passwordStrengthText() {
      switch(this.passwordStrength) {
        case -1: return 'Not entered';
        case 0: return 'Weak (too short)';
        case 1: return 'Medium';
        case 2: return 'Strong';
        default: return '';
      }
    }
  },
  methods: {
    async updatePassword() {
      if (this.newPassword.length < 6 || !this.currentPassword) {
        this.$store.dispatch('showAlert', { 
          message: "Please enter a valid current password and a new password (minimum 6 characters).", 
          type: "error" 
        });
        return;
      }
      try {
        this.$store.commit('setLoading', true);
        await updateUserPassword(this.newPassword, this.currentPassword);
        this.$store.dispatch('showAlert', { 
          message: "Password updated successfully!", 
          type: "success" 
        });
        this.newPassword = "";
        this.currentPassword = "";
        this.showCurrentPassword = false;
        this.showNewPassword = false;
      } catch (error) {
        this.$store.dispatch('showAlert', { 
          message: error.message == "Firebase: Error (auth/invalid-credential)." ? 'Incorrect password' : error.message,
          type: "error" 
        });
      } finally {
        this.$store.commit('setLoading', false);
      }
    }
  }
};
</script>

<style scoped>
/* Custom styling for password toggle buttons */
button[type="button"] {
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
}
</style>